import { api, apiErrorMessage } from '@ddone/api-client';
import { Button, Card, ErrorState, Field, Input, LoadingScreen } from '@ddone/ui';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { normalizeTimer, timerQueryKey, type WireTimer } from '../components/active-timer';
import {
  checkDesktopUpdate,
  desktopBootstrap,
  installDesktopUpdate,
  isTauriDesktop,
  listenForDesktopEvent,
  listenForDesktopOAuth,
  selectDesktopServer,
  setDesktopUpdaterEnabled,
  showDesktopNotification,
  syncDesktopTimer,
  type DesktopBootstrap,
  type DesktopUpdateStatus
} from './tauri-bridge';

interface DesktopRuntimeValue {
  isDesktop: boolean;
  bootstrap?: DesktopBootstrap;
  setUpdaterEnabled(enabled: boolean): Promise<void>;
  checkUpdate(): Promise<DesktopUpdateStatus>;
  installUpdate(): Promise<void>;
  notify(title: string, body: string): Promise<void>;
}

const DesktopRuntimeContext = createContext<DesktopRuntimeValue>({
  isDesktop: false,
  setUpdaterEnabled: () => Promise.resolve(),
  checkUpdate: () =>
    Promise.resolve({
      enabled: false,
      available: false,
      currentVersion: 'web'
    }),
  installUpdate: () => Promise.resolve(),
  notify: () => Promise.resolve()
});

export function DesktopRuntimeProvider({ children }: { children: ReactNode }) {
  const desktop = isTauriDesktop();
  const queryClient = useQueryClient();
  const [bootstrap, setBootstrap] = useState<DesktopBootstrap>();
  const [serverUrl, setServerUrl] = useState('http://127.0.0.1:8080');
  const [pending, setPending] = useState(desktop);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!desktop) return;
    let disposed = false;
    const cleanups: Array<() => void> = [];
    const initialize = async () => {
      try {
        const current = await desktopBootstrap();
        if (disposed) return;
        setBootstrap(current);
        if (current.selectedServer) setServerUrl(current.selectedServer);
        cleanups.push(
          await listenForDesktopOAuth(
            () => window.location.reload(),
            (cause) => setError(errorMessage(cause))
          )
        );
        cleanups.push(
          await listenForDesktopEvent('desktop-timer-start-requested', async () => {
            try {
              const timer = await api.request<WireTimer>('/timer/start', {
                method: 'POST',
                body: { description: 'Timer desktop', billable: false }
              });
              await syncDesktopTimer(normalizeTimer(timer));
              await queryClient.invalidateQueries({ queryKey: timerQueryKey });
            } catch (cause) {
              setError(apiErrorMessage(cause));
            }
          })
        );
        cleanups.push(
          await listenForDesktopEvent('desktop-timer-stop-requested', async () => {
            try {
              await api.request('/timer/stop', { method: 'POST', body: {} });
              await syncDesktopTimer(null);
              await queryClient.invalidateQueries({ queryKey: timerQueryKey });
            } catch (cause) {
              setError(apiErrorMessage(cause));
            }
          })
        );
        cleanups.push(
          await listenForDesktopEvent('desktop-timer-changed', async () => {
            await queryClient.invalidateQueries({ queryKey: timerQueryKey });
          })
        );
      } catch (cause) {
        if (!disposed) setError(errorMessage(cause));
      } finally {
        if (!disposed) setPending(false);
      }
    };
    void initialize();
    return () => {
      disposed = true;
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [desktop, queryClient]);

  const value = useMemo<DesktopRuntimeValue>(
    () => ({
      isDesktop: desktop,
      ...(bootstrap ? { bootstrap } : {}),
      async setUpdaterEnabled(enabled) {
        await setDesktopUpdaterEnabled(enabled);
        setBootstrap((current) => (current ? { ...current, updaterEnabled: enabled } : current));
      },
      checkUpdate: checkDesktopUpdate,
      installUpdate: installDesktopUpdate,
      notify: showDesktopNotification
    }),
    [bootstrap, desktop]
  );

  if (!desktop) {
    return (
      <DesktopRuntimeContext.Provider value={value}>{children}</DesktopRuntimeContext.Provider>
    );
  }
  if (pending) return <LoadingScreen label="Avvio DDone Desktop…" />;
  if (!bootstrap?.selectedServer) {
    return (
      <main className="dd-standalone-state">
        <Card className="dd-desktop-setup">
          <p className="dd-eyebrow">DDone Desktop</p>
          <h1>Collega il server privato</h1>
          <p>È richiesto HTTPS; HTTP è accettato soltanto per localhost.</p>
          {error ? <ErrorState title="Server non configurato" message={error} /> : null}
          <Field label="URL server" htmlFor="desktop-server">
            <Input
              id="desktop-server"
              type="url"
              value={serverUrl}
              onChange={(event) => setServerUrl(event.target.value)}
            />
          </Field>
          <Button
            onClick={() => {
              setPending(true);
              setError(undefined);
              void selectDesktopServer(serverUrl)
                .then(setBootstrap)
                .catch((cause: unknown) => setError(errorMessage(cause)))
                .finally(() => setPending(false));
            }}
          >
            Collega server
          </Button>
        </Card>
      </main>
    );
  }
  return (
    <DesktopRuntimeContext.Provider value={value}>
      {error ? <p className="dd-desktop-runtime-error">{error}</p> : null}
      {children}
    </DesktopRuntimeContext.Provider>
  );
}

export function useDesktopRuntime(): DesktopRuntimeValue {
  return useContext(DesktopRuntimeContext);
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String(error.message);
  }
  return 'Operazione desktop non riuscita.';
}
